<template>
  <div class="fixed bottom-4 right-4 z-50" dir="ltr">
    <!-- Chat Button -->
    <button
      @click="toggleChat"
      class="bg-[#0f4640] rounded-full p-4 text-[#faf6df] shadow-xl hover:bg-[#1f9285] transition-all duration-300 transform hover:scale-110 hover:shadow-2xl relative group"
      :class="{ 'hidden': isChatOpen }"
    >
      <div class="relative">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 transform group-hover:rotate-12 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
        <span class="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-[#faf6df] animate-pulse"></span>
      </div>
      <span class="absolute -top-2 -left-2 w-4 h-4 bg-[#0f4640] rounded-full animate-ping"></span>
    </button>

    <!-- Chat Window -->
    <div v-if="isChatOpen" class="fixed bottom-4 right-4 w-96 h-[500px] chat-window-mobile bg-white rounded-2xl shadow-2xl flex flex-col transition-all duration-300 transform scale-100 overflow-hidden border border-[#0f4640]/10">
      <!-- Header -->
      <div class="p-4 bg-gradient-to-r from-[#0f4640] to-[#1f9285] text-[#faf6df] rounded-t-2xl flex justify-between items-center relative overflow-hidden">
        <div class="flex items-center space-x-3 relative z-10">
          <div class="relative">
            <div class="w-10 h-10 rounded-full bg-[#faf6df]/10 flex items-center justify-center backdrop-blur-sm border border-[#faf6df]/20">
              <span class="text-lg font-semibold">S</span>
            </div>
            <div class="w-3 h-3 rounded-full bg-green-400 absolute -bottom-0.5 -right-0.5 border-2 border-[#faf6df] animate-pulse"></div>
          </div>
          <div>
            <h3 class="font-semibold text-lg">Salasah Assistant</h3>
            <p class="text-xs text-[#faf6df]/80 flex items-center">
              <span class="w-2 h-2 rounded-full bg-green-400 mr-2 animate-pulse"></span>
              Online | Ready to help
            </p>
          </div>
        </div>
        <button @click="toggleChat" class="hover:bg-[#faf6df]/10 p-2 rounded-lg transition-colors relative z-10">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <!-- Decorative pattern -->
        <div class="absolute top-0 left-0 w-full h-full opacity-30 pattern-bg"></div>
      </div>

      <!-- Messages -->
      <div class="flex-1 p-4 overflow-y-auto bg-white" ref="messageContainer">
        <div class="flex flex-col min-h-full">
          <div class="flex-1">
            <div v-for="(message, index) in messages" :key="index" class="mb-4 animate-fade-in">
              <div :class="[
                'p-3 rounded-lg max-w-[90%] shadow-sm',
                message.role === 'user' 
                  ? 'bg-[#0f4640] text-white ml-auto rounded-br-none' 
                  : 'bg-gray-50 border border-gray-200 rounded-bl-none text-[#0f4640]'
              ]">
                <div class="flex items-start gap-2">
                  <div v-if="message.role !== 'user'" class="w-6 h-6 rounded-full bg-[#0f4640] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">
                    س
                  </div>
                  <div class="text-sm leading-relaxed whitespace-pre-line flex-1">
                    {{ message.content }}
                  </div>
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1 px-1" :class="message.role === 'user' ? 'text-right' : 'text-left'">
                <span v-if="message.role !== 'user'" class="flex items-center">
                  <span class="w-2 h-2 rounded-full bg-green-500 ml-1 animate-pulse"></span>
                  مساعد سلاسة
                </span>
                <span v-else>أنت</span>
              </div>
            </div>
          </div>
          <div v-if="isLoading" class="flex justify-start items-center p-3">
            <div class="flex items-center gap-2">
              <div class="w-6 h-6 rounded-full bg-[#0f4640] text-white flex items-center justify-center text-xs font-bold">
                س
              </div>
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span class="text-sm text-gray-500">جاري الكتابة...</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Suggested Questions -->
      <div v-if="messages.length === 1" class="px-4 py-2 bg-white border-t border-[#0f4640]/10">
        <button 
          @click="showSuggestions = !showSuggestions" 
          class="flex items-center justify-between w-full text-sm font-medium text-[#0f4640] hover:text-[#1f9285] transition-colors"
        >
          <span>أسئلة مقترحة</span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            class="h-4 w-4 transform transition-transform duration-200"
            :class="showSuggestions ? 'rotate-180' : ''"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div 
          v-show="showSuggestions"
          class="mt-2 grid grid-cols-1 gap-1.5 overflow-hidden transition-all duration-200"
        >
          <button
            v-for="question in suggestedQuestions"
            :key="question"
            @click="askQuestion(question)"
            class="text-sm px-3 py-2 rounded-lg border border-[#0f4640]/20 hover:border-[#0f4640] hover:text-white hover:bg-[#0f4640] transition-all duration-200 text-right text-[#0f4640] bg-white"
          >
            {{ question }}
          </button>
        </div>
      </div>

      <!-- Input -->
      <div class="p-4 bg-white border-t border-[#0f4640]/10">
        <form @submit.prevent="sendMessage" class="flex gap-2">
          <div class="flex-1 relative">
            <input
              v-model="userInput"
              type="text"
              placeholder="اكتب رسالتك هنا..."
              class="w-full p-3 pr-10 border border-[#0f4640]/20 rounded-xl focus:outline-none focus:border-[#0f4640] focus:ring-2 focus:ring-[#0f4640]/20 transition-all duration-200 placeholder-gray-400 text-[#0f4640] bg-white"
              :disabled="isLoading || hasError"
            />
            <span v-if="!isLoading && userInput.trim()" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
              اضغط Enter ↵
            </span>
          </div>
          <button
            type="submit"
            class="bg-gradient-to-r from-[#0f4640] to-[#1f9285] text-[#faf6df] px-5 py-3 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:hover:scale-100 disabled:hover:shadow-none relative overflow-hidden group"
            :disabled="isLoading || !userInput.trim() || hasError"
          >
            <span v-if="!isLoading" class="flex items-center gap-2 relative z-10">
              إرسال
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </span>
            <span v-else class="flex items-center gap-2 relative z-10">
              <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              جاري المعالجة...
            </span>
            <div class="absolute inset-0 bg-gradient-to-r from-[#1f9285] to-[#0f4640] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick } from 'vue'
import { GoogleGenerativeAI } from '@google/generative-ai'

export default {
  name: 'ChatBot',
  setup() {
    const isChatOpen = ref(false)
    const messages = ref([
      {
        role: 'assistant',
        content: `مرحباً بك في سلاسة! 👋

أنا مساعدك الشخصي، هنا لمساعدتك في:
• معلومات عن خدماتنا
• الدعم الفني
• الحلول التجارية
• الأسئلة العامة

كيف يمكنني مساعدتك اليوم؟`
      }
    ])
    const userInput = ref('')
    const isLoading = ref(false)
    const messageContainer = ref(null)
    const hasError = ref(false)
    const showSuggestions = ref(false)

    // Initialize Gemini AI
    const genAI = new GoogleGenerativeAI('AIzaSyAoYamjOD_PE-ncsv2ekabsAZvThA98vTw')
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' })

    const suggestedQuestions = [
      'ما هي خدمات سلاسة؟',
      'كيف أصدر فاتورة إلكترونية؟',
      'هل أنتم معتمدون من هيئة الزكاة؟',
      'كيف أتواصل مع الدعم الفني؟'
    ]

    const systemPrompt = `أنت مساعد سلاسة — مساعد افتراضي مهني وودود لشركة سلاسة، منصة سعودية تقدم حلول الفوترة الإلكترونية والمحاسبة.

مهمتك هي:

1. مساعدة المستخدمين على فهم خدمات سلاسة:
   - أنظمة ERP و CRM
   - الفوترة الإلكترونية (معتمد من هيئة الزكاة والضريبة والجمارك)
   - لوحات معلومات ذكية للأعمال
   - حلول التحول الرقمي
   - البنية التحتية السحابية والبرمجيات المخصصة
   - إدارة الموارد البشرية
   - إدارة المشاريع
   - إدارة علاقات العملاء

2. التعامل مع الأسئلة الشائعة:
   - "كيف أصدر فاتورة إلكترونية؟"
   - "ما هي خدمات سلاسة؟"
   - "هل أنتم معتمدون من هيئة الزكاة والدخل؟"
   - "هل أستطيع تجربة النظام؟"
   - "كيف أتواصل مع الدعم الفني؟"
   - "ما هي أسعار الخدمات؟"
   - "كيف يمكنني البدء؟"

3. الرد بوضوح باللغة العربية أو الإنجليزية حسب لغة المستخدم. الافتراضي هو العربية.

4. كن مهذباً ودقيقاً ومختصراً وموجهاً نحو الحلول.

5. المعلومات المهمة التي يجب معرفتها:
   - سلاسة معتمد من هيئة الزكاة والضريبة والجمارك
   - نقدم نظام محاسبة وفوترة إلكتروني متكامل
   - دعم كامل للشركات السعودية
   - واجهة عربية سهلة الاستخدام
   - تكامل مع أنظمة الدفع المحلية (مدى، STC Pay)
   - دعم فني على مدار الساعة
   - يمكن تجربة النظام عبر: https://admin.salasah.sa/#/login
   - للدعم الفني: support@salasah.sa أو اتصل على +966 56 173 0527
   - للمبيعات والاستفسارات: info@salasah.sa

6. إذا كان طلب المستخدم خارج نطاق عملك، أجب بـ:
   "لطفاً تواصل مع الدعم الفني لمساعدتك بشكل أفضل على support@salasah.sa أو اتصل على +966 56 173 0527"

لا تخترع ميزات المنتج أو تقدم التزامات. مهمتك هي الإعلام والمساعدة وإعادة التوجيه عند الحاجة.

تذكر: كن مفيداً ومهنياً وصادقاً في جميع استجاباتك.`

    const toggleChat = () => {
      isChatOpen.value = !isChatOpen.value
      if (isChatOpen.value && hasError.value) {
        hasError.value = false
        messages.value = [
          {
            role: 'assistant',
            content: `مرحباً بك في سلاسة! 👋

أنا مساعدك الشخصي، هنا لمساعدتك في:
• معلومات عن خدماتنا
• الدعم الفني
• الحلول التجارية
• الأسئلة العامة

كيف يمكنني مساعدتك اليوم؟`
          }
        ]
      }
    }

    const scrollToLastMessage = async () => {
      await nextTick()
      if (messageContainer.value) {
        const lastMessage = messageContainer.value.querySelector('.animate-fade-in:last-child')
        if (lastMessage) {
          lastMessage.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    }

    const askQuestion = (question) => {
      userInput.value = question
      sendMessage()
    }

    const handleError = (error) => {
      console.error('Chat error:', error)
      hasError.value = true
      
      let errorMessage = `عذراً، واجهت مشكلة تقنية. يرجى المحاولة مرة أخرى.

للمساعدة الفورية:
📧 support@salasah.sa
📞 +966 56 173 0527`
      
      if (error.message && (error.message.includes('API_KEY') || error.message.includes('403') || error.message.includes('401'))) {
        errorMessage = `خطأ في الوصول للذكاء الاصطناعي. يرجى التواصل مع الدعم الفني.

📧 support@salasah.sa
📞 +966 56 173 0527`
      } else if (error.message && error.message.includes('fetch')) {
        errorMessage = `مشكلة في الاتصال بالإنترنت. تأكد من اتصالك وحاول مرة أخرى.

للمساعدة الفورية:
📧 support@salasah.sa
📞 +966 56 173 0527`
      }

      messages.value.push({
        role: 'assistant',
        content: errorMessage
      })
      scrollToLastMessage()
    }

    const sendMessage = async () => {
      if (!userInput.value.trim() || isLoading.value) return

      const userMessage = userInput.value.trim()
      messages.value.push({ role: 'user', content: userMessage })
      await scrollToLastMessage()
      userInput.value = ''
      isLoading.value = true
      hasError.value = false

      try {
        console.log('Sending message to Gemini AI...')
        
        // Prepare conversation history for Gemini
        const conversationHistory = messages.value
          .filter(msg => msg.role !== 'assistant' || !msg.content.includes('مرحباً بك في سلاسة'))
          .map(msg => `${msg.role === 'user' ? 'المستخدم' : 'المساعد'}: ${msg.content}`)
          .join('\n\n')

        const prompt = `${systemPrompt}

المحادثة السابقة:
${conversationHistory}

المستخدم: ${userMessage}

المساعد:`

        // Add timeout and better error handling
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 30000)
        )

        const apiPromise = model.generateContent(prompt)
        
        const result = await Promise.race([apiPromise, timeoutPromise])
        const response = await result.response
        const text = response.text()

        console.log('Gemini AI response received:', text)
        
        if (text && text.trim()) {
          messages.value.push({ role: 'assistant', content: text.trim() })
          await scrollToLastMessage()
        } else {
          throw new Error('Empty response from Gemini AI')
        }
      } catch (error) {
        console.error('Gemini AI error:', error)
        
        // Fallback response for common questions
        const fallbackResponse = getFallbackResponse(userMessage)
        if (fallbackResponse) {
          messages.value.push({ role: 'assistant', content: fallbackResponse })
          await scrollToLastMessage()
        } else {
          handleError(error)
        }
      } finally {
        isLoading.value = false
      }
    }

    const getFallbackResponse = (message) => {
      const lowerMessage = message.toLowerCase()
      
      if (lowerMessage.includes('خدمات') || lowerMessage.includes('services') || lowerMessage.includes('ما هي')) {
        return `نحن في سلاسة نقدم الخدمات التالية:

📊 نظام المحاسبة والفوترة الإلكترونية
- معتمد من هيئة الزكاة والضريبة والجمارك
- فوترة إلكترونية متوافقة مع المتطلبات السعودية

💼 أنظمة إدارة الأعمال
- نظام إدارة علاقات العملاء (CRM)
- نظام تخطيط موارد المؤسسة (ERP)
- إدارة الموارد البشرية

📈 حلول ذكية
- لوحات معلومات تفاعلية
- تقارير مالية متقدمة
- تحليلات الأعمال

للمزيد من المعلومات:
📧 info@salasah.sa
📞 +966 56 173 0527
🌐 تجربة مجانية: https://admin.salasah.sa/#/login`
      }
      
      if (lowerMessage.includes('فاتورة') || lowerMessage.includes('invoice') || lowerMessage.includes('كيف')) {
        return `كيفية إصدار فاتورة إلكترونية مع سلاسة:

1️⃣ تسجيل الدخول: ادخل على النظام
2️⃣ إنشاء فاتورة: اختر "فاتورة جديدة"
3️⃣ إدخال البيانات: أضف بيانات العميل والخدمات
4️⃣ المراجعة: تأكد من صحة البيانات
5️⃣ الإرسال: أرسل الفاتورة للعميل وهيئة الزكاة

✅ مميزات فواتير سلاسة:
- متوافقة 100% مع هيئة الزكاة والضريبة
- إرسال تلقائي للجهات الحكومية
- تصميم احترافي باللغة العربية
- دعم للرموز الشريطية (QR Code)

للتجربة المجانية:
🌐 https://admin.salasah.sa/#/login
📞 +966 56 173 0527`
      }
      
      if (lowerMessage.includes('معتمد') || lowerMessage.includes('زكاة') || lowerMessage.includes('zatca') || lowerMessage.includes('approved')) {
        return `✅ نعم، سلاسة معتمد رسمياً من هيئة الزكاة والضريبة والجمارك

🏛️ التراخيص والاعتمادات:
- مُعتمد لإصدار الفواتير الإلكترونية
- متوافق مع جميع متطلبات المرحلة الثانية
- يدعم جميع أنواع الفواتير (B2B & B2C)

📋 المعايير المطبقة:
- معيار الفاتورة الإلكترونية السعودية
- تشفير وأمان البيانات
- الإرسال المباشر للهيئة

🔒 الأمان والثقة:
- حماية عالية للبيانات
- نسخ احتياطية آمنة
- دعم فني مختص

للتأكد من الاعتماد:
📞 +966 56 173 0527
📧 info@salasah.sa`
      }
      
      if (lowerMessage.includes('دعم') || lowerMessage.includes('support') || lowerMessage.includes('مساعدة') || lowerMessage.includes('تواصل')) {
        return `📞 كيفية التواصل مع فريق الدعم في سلاسة:

🕐 الدعم الفني (متاح 24/7):
📞 الهاتف: +966 56 173 0527
📧 البريد: support@salasah.sa

💼 المبيعات والاستفسارات التجارية:
📧 البريد: info@salasah.sa
📞 الهاتف: +966 56 173 0527

🌐 الدعم الإلكتروني:
- تجربة مجانية: https://admin.salasah.sa/#/login
- موقعنا الإلكتروني: salasah.sa

⚡ الاستجابة السريعة:
- رد فوري على الاستفسارات العاجلة
- دعم تقني مختص باللغة العربية
- حلول سريعة للمشاكل التقنية

نحن هنا لمساعدتك في أي وقت! 🚀`
      }
      
      return null
    }

    return {
      isChatOpen,
      messages,
      userInput,
      isLoading,
      messageContainer,
      hasError,
      suggestedQuestions,
      toggleChat,
      sendMessage,
      askQuestion,
      showSuggestions
    }
  }
}
</script>

<style scoped>
.pattern-bg {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23faf6df' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Typing indicator animation */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(15, 70, 64, 0.1);
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background-color: #0f4640;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
  opacity: 0.3;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.4s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes typing {
  0%, 100% {
    transform: translateY(0);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

/* Message animation */
.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Scrollbar styling */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 70, 64, 0.2) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(15, 70, 64, 0.2);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(15, 70, 64, 0.4);
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .fixed.bottom-4.right-4 {
    bottom: 1rem;
    right: 1rem;
  }

  .w-96 {
    width: calc(100vw - 2rem);
    max-width: 24rem;
  }

  .chat-window-mobile {
    height: 80vh;
    max-height: 500px;
  }
}
</style> 